import { Container, Graphics, Text } from "pixi.js"
import { TEXT_ROLES } from "../text/typography"
import { TextRenderer } from "../text/text_renderer"
import {
    DIAL_CAPTION_Y,
    DIAL_VALUE_Y,
    DialLabel,
    DialMetrics,
    dialBottomExtent,
    dialSize,
    drawDialFace,
    drawDialGauge,
    interpolateColor,
} from "./dial"

// The HUD badge shown while an ember camp is under assault: which wave is being fought and how many
// of its enemies are still to be put down.
//
// It deliberately takes the pre-wave COUNTDOWN DIAL's slot and its shape — same radius, same stroke,
// same dark face and groove. The countdown drains to zero, disappears, and this dial takes its place
// already full, so the HUD reads as one object handing over: "time until the wave" becomes "what's
// left of the wave". The palettes are what separate them, and they run opposite ways: the countdown
// HEATS as safety runs out, this one COOLS from hostile red to ember gold as the threat does.
//
// The gauge is `remaining / total`, where remaining counts enemies not yet released by the spawners
// as well as the ones on the field — so it only ever falls (see ember-wave-manager.js, which owns
// those numbers). A head count would climb while the player fought, which reads backwards.

// Gauge ramp: a full ring is hostile blood-red, a nearly-cleared one is the camp's own ember gold
// (build_board GOLD). It runs the opposite way to the countdown's, which heats UP as time runs out:
// this one cools as the threat does.
const THREAT_COLOR = 0xd63a2a
const CLEARING_COLOR = 0xffd166

const COUNT_COLOR = 0xffffff
const CAPTION_COLOR = 0xffc89a

const COUNT_SIZE = TEXT_ROLES.heading.fontSize
const CAPTION_SIZE = TEXT_ROLES.compactCaption.fontSize
const CAPTION_SPACING = 1.6

const APPEAR_MS = 220
const FLASH_MS = 320
// Below this many enemies the ring breathes — the last of a wave is the part worth watching.
const FINAL_STRETCH = 3

export interface WaveStatus {
    // 1-based wave being fought. Zero (or a null status) means no wave: take the badge down.
    wave: number
    // Enemies left to put down, including any the spawners haven't released yet.
    remaining: number
    // Size of the whole wave; the gauge's denominator.
    total: number
    // Enemies on the field right now. Not drawn — kept so the shape matches what the server sends.
    alive?: number
}

// How full the gauge ring is. Guards the two ways the numbers can arrive unusable: no total yet
// (show a full ring rather than an empty one — the wave has just landed, not been cleared) and a
// remaining count that outruns the total (a late spawn), which would otherwise wrap the arc.
export const gaugeProgress = (remaining: number, total: number): number => {
    if (!(total > 0)) {
        return remaining > 0 ? 1 : 0
    }
    return Math.max(0, Math.min(1, remaining / total))
}

// Red at full, ember gold as it empties.
export const threatColor = (progress: number): number => interpolateColor(CLEARING_COLOR, THREAT_COLOR, progress)

const easeOut = (t: number): number => 1 - Math.pow(1 - t, 3)

export interface WaveIndicatorProps {
    radius?: number
    strokeWidth?: number
}

export class WaveIndicator extends Container {
    private metrics: DialMetrics

    private face: Graphics
    private gauge: Graphics
    private countText: Text
    private captionText: Text
    private label: DialLabel

    // What's being displayed. Null until the first wave status arrives.
    private status: WaveStatus | null = null
    // Whether the badge wants to be on screen; `appear` is how far the entrance/exit has got (0..1).
    private wanted = false
    private appear = 0
    // Kill feedback: driven to 1 when `remaining` drops, decays back to 0.
    private flash = 0
    // Free-running clock for the final-stretch breathing.
    private pulseTs = 0
    private needsDraw = true

    constructor(props: WaveIndicatorProps = {}) {
        super()

        this.metrics = { radius: props.radius || 50, strokeWidth: props.strokeWidth || 7.5 }

        this.face = new Graphics()
        this.gauge = new Graphics()

        this.countText = new TextRenderer().color(COUNT_COLOR).fontSize(COUNT_SIZE).bold().align("center").render("0") as Text
        this.countText.anchor.set(0.5)
        this.captionText = new TextRenderer()
            .color(CAPTION_COLOR)
            .fontSize(CAPTION_SIZE)
            .bold()
            .align("center")
            .render("LEFT") as Text
        this.captionText.anchor.set(0.5)
        this.captionText.style.letterSpacing = CAPTION_SPACING
        this.captionText.alpha = 0.75
        this.label = new DialLabel()

        this.addChild(this.face)
        this.addChild(this.gauge)
        this.addChild(this.label)
        this.addChild(this.countText)
        this.addChild(this.captionText)

        this.drawFace()
        this.layoutText()

        this.visible = false
        this.alpha = 0
    }

    // Outer painted size of the dial — what a caller positions against.
    public get dialSize(): number {
        return dialSize(this.metrics)
    }

    // How far below the dial's CENTRE the badge actually paints. Lets a caller keep the whole thing
    // on screen without reaching into the layout.
    public get bottomExtent(): number {
        return dialBottomExtent(this.metrics)
    }

    // New wave state from the server, or null / wave 0 to take the badge down.
    public update(status: WaveStatus | null) {
        const live = status && status.wave > 0 ? status : null

        if (!live) {
            if (this.wanted) {
                this.wanted = false
            }
            return
        }

        const previous = this.status
        // A drop in the count is a kill (or several): flash the number so the player sees it land.
        // A new wave is not a drop, however much the number falls between waves.
        if (previous && previous.wave === live.wave && live.remaining < previous.remaining) {
            this.flash = 1
        }

        this.status = live
        this.wanted = true
        this.needsDraw = true
    }

    // Per-frame tick. Only redraws when something is actually moving, so a long wave doesn't repaint
    // a static dial 60 times a second.
    public animate(deltaMs: number) {
        const target = this.wanted ? 1 : 0
        if (this.appear !== target) {
            const step = deltaMs / APPEAR_MS
            this.appear = target > this.appear ? Math.min(1, this.appear + step) : Math.max(0, this.appear - step)
            this.needsDraw = true
        }

        if (this.flash > 0) {
            this.flash = Math.max(0, this.flash - deltaMs / FLASH_MS)
            this.needsDraw = true
        }

        if (this.isBreathing()) {
            this.pulseTs += deltaMs
            this.needsDraw = true
        }

        if (!this.needsDraw) {
            return
        }
        this.needsDraw = false
        this.redraw()
    }

    // Whether the badge is on screen at all (mid-fade counts).
    public isShowing(): boolean {
        return this.visible
    }

    // Test/inspection seam: how strong the kill flash is right now (1 the frame a kill lands, 0 once
    // it has decayed).
    public flashStrength(): number {
        return this.flash
    }

    private isBreathing(): boolean {
        return this.wanted && !!this.status && this.status.remaining > 0 && this.status.remaining <= FINAL_STRETCH
    }

    private redraw() {
        this.visible = this.appear > 0
        if (!this.visible || !this.status) {
            this.alpha = 0
            return
        }

        const eased = easeOut(this.appear)
        this.alpha = eased
        // Grows into place rather than popping — the countdown dial it replaces has just vanished
        // from the same spot.
        this.scale.set(0.82 + 0.18 * eased)

        const { remaining, total, wave } = this.status

        if (this.countText.text !== String(remaining)) {
            this.countText.text = String(remaining)
        }
        this.label.setWave(wave)

        // The number swells on a kill and settles back.
        this.countText.scale.set(1 + 0.28 * this.flash)

        this.drawGauge(gaugeProgress(remaining, total))
    }

    private drawFace() {
        // The countdown dial's face, down to the pixel (see dial.ts). There is no "but" any more:
        // the clock hour marks that used to separate them are gone from both.
        drawDialFace(this.face, { ...this.metrics })
    }

    // Which wave, the number, what the number means — on the lines dial.ts fixes, which is what
    // keeps the top line from so much as twitching when the countdown dial hands this one its slot.
    private layoutText() {
        this.countText.y = DIAL_VALUE_Y
        this.captionText.y = DIAL_CAPTION_Y
    }

    private drawGauge(progress: number) {
        if (progress <= 0) {
            this.gauge.clear()
            return
        }

        const color = threatColor(progress)
        // The last of a wave breathes; a kill washes the arc toward white for a moment.
        const breath = this.isBreathing() ? 0.5 + 0.5 * Math.sin(this.pulseTs / 190) : 0
        drawDialGauge(this.gauge, {
            ...this.metrics,
            progress,
            color,
            arcColor: this.flash > 0 ? interpolateColor(color, 0xffffff, this.flash * 0.55) : color,
            haloWidth: 5 + 4 * breath,
            haloAlpha: 0.18 + 0.22 * breath + 0.3 * this.flash,
        })
    }
}
